/**
 * Uploading, rendering and deleting user images.
 *
 * Rendering goes through fetch rather than a plain <img src>, because the API
 * scope-checks every read and an <img> tag can't carry the bearer token. The
 * responses are marked immutable, so the browser's HTTP cache serves repeat
 * views without touching the network — the object URLs below are just handles
 * onto that cache, not a second copy of it.
 */

import { logger } from "../utils/logger"
import { apiRequest, apiUpload, apiUrl } from "./apiClient"

export type ImagePurpose = "avatar" | "household" | "list_cover" | "receipt"

export interface ImageRef {
  id: string
  url: string
  purpose: ImagePurpose
  contentType: string
  bytes: number
  width?: number
  height?: number
  avgColor?: string
  createdAt?: string
}

export interface UploadInput {
  blob: Blob
  purpose: ImagePurpose
  width?: number
  height?: number
  avgColor?: string
  householdId?: string | null
}

const EXTENSIONS: Record<string, string> = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
}

export async function uploadImage(input: UploadInput): Promise<ImageRef> {
  const form = new FormData()
  const extension = EXTENSIONS[input.blob.type] ?? "jpg"
  form.append("file", input.blob, `upload.${extension}`)
  form.append("purpose", input.purpose)
  if (input.width) form.append("width", String(input.width))
  if (input.height) form.append("height", String(input.height))
  if (input.avgColor) form.append("avgColor", input.avgColor)

  const image = await apiUpload<ImageRef>("/api/images", form, {
    householdId: input.householdId,
    // Uploads are ~20-150 KB but a phone on a bad connection is slow, not
    // broken; this only fires when something is genuinely wedged.
    timeoutMs: 60_000,
  })
  return image
}

/**
 * Upload a receipt that was captured as a data URL (the AI parser needs one
 * in-hand before the expense exists). Returns null on failure: a receipt photo
 * is an attachment, never a reason to lose the transaction the user just
 * typed — the caller falls back to the legacy data-URL column.
 */
export async function uploadReceiptDataUrl(dataUrl: string, householdId?: string | null): Promise<string | null> {
  try {
    const { prepareImage, RECEIPT_OPTIONS } = await import("./imageCapture")
    const prepared = await prepareImage(dataUrl, RECEIPT_OPTIONS)
    const image = await uploadImage({
      blob: prepared.blob,
      purpose: "receipt",
      width: prepared.width,
      height: prepared.height,
      avgColor: prepared.avgColor,
      householdId,
    })
    return image.id
  } catch (error) {
    logger.warn("Failed to upload receipt image", { error })
    return null
  }
}

export async function deleteImage(imageId: string): Promise<void> {
  await apiRequest(`/api/images/${imageId}`, { method: "DELETE" })
  releaseImage(imageId)
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const objectUrls = new Map<string, Promise<string>>()

/**
 * Object URL for an image id, fetched once per session and memoised. The
 * promise is cached (not the resolved string) so ten avatars rendering at once
 * share one request.
 */
export function imageObjectUrl(imageId: string): Promise<string> {
  const cached = objectUrls.get(imageId)
  if (cached) return cached

  const pending = (async () => {
    const response = await fetch(apiUrl(`/api/images/${imageId}`), {
      headers: await authHeaders(),
    })
    if (!response.ok) throw new Error(`Image ${imageId} failed (${response.status})`)
    return URL.createObjectURL(await response.blob())
  })()

  // A failed fetch must not poison the cache: an offline first render should
  // retry when the user comes back, not show a broken tile forever.
  pending.catch(() => objectUrls.delete(imageId))
  objectUrls.set(imageId, pending)
  return pending
}

async function authHeaders(): Promise<Record<string, string>> {
  const { authService } = await import("./authService")
  const token = await authService.getIdToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Drop one image from the session cache (after replacing or deleting it). */
export function releaseImage(imageId: string): void {
  const pending = objectUrls.get(imageId)
  objectUrls.delete(imageId)
  pending
    ?.then((url) => URL.revokeObjectURL(url))
    .catch(() => {
      /* never rendered */
    })
}

/** Drop every cached object URL — called on sign-out. */
export function clearImageCache(): void {
  for (const imageId of [...objectUrls.keys()]) releaseImage(imageId)
}

/**
 * Load an image for display, tolerating failure: a missing or unreachable
 * photo falls back to whatever placeholder the caller already renders rather
 * than breaking the surface it sits on.
 */
export async function tryImageObjectUrl(imageId: string | null | undefined): Promise<string | null> {
  if (!imageId) return null
  try {
    return await imageObjectUrl(imageId)
  } catch (error) {
    logger.warn("Failed to load image", { imageId, error })
    return null
  }
}
